import { useQuery } from '@tanstack/react-query';
import { apiClient, Product } from '../services/api';

// Enums matching backend SearchProductsDto
export enum SortField {
  NAME = 'name',
  PRICE = 'price',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  CATEGORY = 'category',
  BRAND = 'brand',
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum StockStatus {
  IN_STOCK = 'in_stock',
  LOW_STOCK = 'low_stock',
  OUT_OF_STOCK = 'out_of_stock',
  ALL = 'all',
}

// Search parameters interface matching backend SearchProductsDto
export interface ProductSearchParams {
  q?: string;
  category?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  locationId?: string;
  stockStatus?: StockStatus;
  isVisibleInStorefront?: boolean;
  isB2bVisible?: boolean;
  productType?: string;
  page?: number;
  limit?: number;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  saveSearch?: boolean;
}

// Search result interface
export interface ProductSearchResult {
  data: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Suggestion interface
export interface SearchSuggestion {
  type: 'product' | 'category' | 'brand';
  value: string;
  count?: number;
}

// Recent search interface
export interface RecentSearch {
  id: string;
  searchQuery: string;
  filters?: Record<string, any>;
  resultCount: number;
  createdAt: string;
}

// API functions for product search
export const productSearchApi = {
  search: (params?: ProductSearchParams) =>
    apiClient
      .get<ProductSearchResult>('/products/search', { params })
      .then((res) => res.data),

  getSuggestions: (query: string) =>
    apiClient
      .get<SearchSuggestion[]>('/products/search/suggestions', {
        params: { q: query },
      })
      .then((res) => res.data),

  getRecentSearches: (limit?: number) =>
    apiClient
      .get<RecentSearch[]>('/products/search/recent', {
        params: { limit: limit || 10 },
      })
      .then((res) => res.data),
};

// Main search hook
export const useProductSearch = (params?: ProductSearchParams) => {
  return useQuery({
    queryKey: ['products', 'search', params],
    queryFn: () => productSearchApi.search(params),
    enabled: !!params?.q || !!params?.category || !!params?.brand || !!params?.locationId,
  });
};

// Search suggestions hook
export const useSearchSuggestions = (query: string) => {
  return useQuery({
    queryKey: ['products', 'search', 'suggestions', query],
    queryFn: () => productSearchApi.getSuggestions(query),
    enabled: !!query && query.length >= 2,
  });
};

// Recent searches hook
export const useRecentSearches = (limit?: number) => {
  return useQuery({
    queryKey: ['products', 'search', 'recent', limit],
    queryFn: () => productSearchApi.getRecentSearches(limit),
  });
};
